import { Link } from "react-router-dom";
import BrandLogo from "@/components/BrandLogo";
import { Mail, KeyRound, CreditCard, Headset, ShieldCheck } from "lucide-react";
import useSeo from "@/hooks/useSeo";

const SECTIONS = [
  {
    icon: KeyRound,
    title: "Account access & password reset",
    body: (
      <>
        If you can't sign in, use the{" "}
        <Link to="/forgot-password" className="font-semibold underline underline-offset-2" data-testid="support-forgot-link">
          password reset page
        </Link>{" "}
        — reset links expire after 30 minutes and can only be used once. If the reset email doesn't
        arrive, check spam, then email us and we'll help you regain access.
      </>
    ),
  },
  {
    icon: CreditCard,
    title: "Billing & subscriptions",
    body: (
      <>
        Manage your plan from Settings → Billing in your dashboard. The price you agree when you
        subscribe stays fixed for your subscription — plan price changes never alter an existing
        subscription mid-term. For refunds, cancellations or invoice questions, email us with your
        account email and we'll confirm what's on your account before making any change.
      </>
    ),
  },
  {
    icon: Headset,
    title: "Technical help",
    body: (
      <>
        For help with AI Voice Agents, the CRM, the AI Marketing Studio or integrations, email us
        with a description of what you were trying to do and what happened. Screenshots help.
        Features marked "Coming soon" or "Pending setup" need provider configuration first — we can
        tell you exactly what's required for yours.
      </>
    ),
  },
  {
    icon: ShieldCheck,
    title: "Privacy & security enquiries",
    body: (
      <>
        For data access, correction or deletion requests, or to report a security concern, email us
        from the address on your account. Details of what we store are in our{" "}
        <Link to="/privacy" className="font-semibold underline underline-offset-2">Privacy Policy</Link>.
      </>
    ),
  },
];

export default function Support() {
  useSeo("Support — Savy Kerno", "Get help with Savy Kerno: account access, password reset, billing and product questions. Email support@savykernoai.com.");
  return (
    <div className="min-h-screen bg-white" data-testid="support-page">
      <header className="border-b border-neutral-200">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-5 py-4">
          <Link to="/" className="flex items-center gap-2.5" data-testid="support-brand">
            <BrandLogo />
            <span className="text-base font-bold tracking-tight">Savy Kerno</span>
          </Link>
          <Link to="/login" className="text-sm font-medium text-neutral-600 hover:text-[#111111]" data-testid="support-signin">
            Sign In
          </Link>
        </div>
      </header>
      <main className="mx-auto max-w-4xl px-5 py-14">
        <h1 className="text-3xl font-bold tracking-tight">Support</h1>
        <p className="mt-4 max-w-2xl text-sm leading-relaxed text-neutral-600">
          We're a small team and we read every message. The fastest way to reach us is email.
        </p>

        <section className="mt-10 rounded-2xl border border-neutral-200 bg-neutral-50/60 p-6" data-testid="support-contact">
          <div className="flex items-center gap-2.5">
            <Mail size={16} className="text-[#111111]" />
            <h2 className="text-base font-bold">Contact Support</h2>
          </div>
          <a
            href="mailto:support@savykernoai.com"
            data-testid="support-email-link"
            className="mt-3 inline-block text-lg font-semibold text-[#111111] underline underline-offset-4 hover:text-neutral-600"
          >
            support@savykernoai.com
          </a>
          <p className="mt-2 text-xs text-neutral-500">
            We aim to reply within one business day. Please email from the address on your account so we can verify you.
          </p>
        </section>

        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          {SECTIONS.map((s) => {
            const Icon = s.icon;
            return (
              <section key={s.title} className="rounded-2xl border border-neutral-200 p-6" data-testid={`support-section-${s.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <div className="flex items-center gap-2.5">
                  <Icon size={16} className="text-[#111111]" />
                  <h2 className="text-base font-bold">{s.title}</h2>
                </div>
                <p className="mt-2 text-sm leading-relaxed text-neutral-600">{s.body}</p>
              </section>
            );
          })}
        </div>
      </main>
    </div>
  );
}
